import { useEffect, useMemo, useState } from 'react'
import {
  BookOpenCheck,
  Boxes,
  ChevronRight,
  CircleAlert,
  Package,
  ShieldCheck,
} from 'lucide-react'
import { toast } from 'sonner'
import { ApiError, isTransportError } from '@/api/client'
import {
  type KnowledgeSummary,
  type ServiceCaseFilters,
  type ServiceCaseHierarchyNode,
  type ServiceCaseSelection,
  useApproveServiceKnowledge,
  useCorrectServiceKnowledge,
  usePromoteServiceKnowledge,
  useRejectServiceKnowledge,
  useRetireServiceKnowledge,
  useServiceCaseDefects,
  useServiceCaseHierarchy,
  useServiceCaseKnowledge,
  useServiceCaseObservations,
} from '@/api/productKnowledgeServiceCases'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'

type ReviewAction = 'approve' | 'reject' | 'correct' | 'retire'

const SCOPE_LABEL = {
  group: 'Artikelgruppe',
  base_product: 'Grundartikel',
  variant: 'Variante',
} as const

const STATUS_LABEL = {
  proposed: 'Zur Prüfung',
  approved: 'Freigegeben',
  rejected: 'Abgelehnt',
  retired: 'Stillgelegt',
} as const

function sameSelection(left: ServiceCaseSelection | null, right: ServiceCaseSelection): boolean {
  return left?.scope === right.scope && left.scopeId === right.scopeId
}

function HierarchyItem({
  node,
  selected,
  depth = 0,
  onSelect,
}: {
  node: ServiceCaseHierarchyNode
  selected: ServiceCaseSelection | null
  depth?: number
  onSelect: (node: ServiceCaseHierarchyNode) => void
}) {
  const Icon = node.scope === 'group' ? Boxes : node.scope === 'base_product' ? Package : ChevronRight
  return (
    <li>
      <button
        type="button"
        aria-pressed={sameSelection(selected, node)}
        className={cn(
          'flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm hover:bg-muted',
          sameSelection(selected, node) && 'bg-primary-soft text-primary font-medium',
        )}
        style={{ paddingLeft: `${8 + depth * 18}px` }}
        onClick={() => onSelect(node)}
      >
        <Icon className="size-4 shrink-0" />
        <span className="min-w-0 flex-1 truncate">
          {node.sku ? `${node.sku} · ` : ''}{node.name}
        </span>
        <Badge variant="outline">{node.caseCount}</Badge>
      </button>
      {node.children.length > 0 && (
        <ul>
          {node.children.map((child) => (
            <HierarchyItem
              key={`${child.scope}:${child.scopeId}`}
              node={child}
              selected={selected}
              depth={depth + 1}
              onSelect={onSelect}
            />
          ))}
        </ul>
      )}
    </li>
  )
}

function StatusBadge({ status }: { status: KnowledgeSummary['status'] }) {
  const variant = status === 'approved'
    ? 'default'
    : status === 'rejected'
      ? 'destructive'
      : 'outline'
  return <Badge variant={variant}>{STATUS_LABEL[status]}</Badge>
}

function isDecodedConflict(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status === 409 && !error.undecodable
}

function formatError(error: unknown): string {
  if (isDecodedConflict(error)) {
    return `${error.message} Die angezeigten Daten werden aktualisiert; bitte danach erneut pruefen.`
  }
  return error instanceof Error ? error.message : 'Die Aktion ist fehlgeschlagen.'
}

export default function ServiceCasesPage() {
  const [filters, setFilters] = useState<ServiceCaseFilters>({})
  const [draftFilters, setDraftFilters] = useState<ServiceCaseFilters>({})
  const [selection, setSelection] = useState<ServiceCaseSelection | null>(null)
  const [defectCode, setDefectCode] = useState<string | null>(null)
  const [review, setReview] = useState<{
    action: ReviewAction
    knowledge: KnowledgeSummary
    requestId: string
    lockedPayload?: { reason?: string; safeStatement?: string }
  } | null>(null)
  const [reason, setReason] = useState('')
  const [statement, setStatement] = useState('')
  const [promoteOpen, setPromoteOpen] = useState(false)
  const [promotionRequestId, setPromotionRequestId] = useState<string | null>(null)
  const [lockedPromotion, setLockedPromotion] = useState<{
    safeStatement: string
    observationIds: string[]
  } | null>(null)
  const [selectedEvidence, setSelectedEvidence] = useState<string[]>([])
  const [promoteStatement, setPromoteStatement] = useState('')

  const hierarchy = useServiceCaseHierarchy(filters)
  const defects = useServiceCaseDefects(selection, filters)
  const observations = useServiceCaseObservations(selection, defectCode, filters)
  const knowledge = useServiceCaseKnowledge(selection, defectCode)
  const approve = useApproveServiceKnowledge()
  const reject = useRejectServiceKnowledge()
  const correct = useCorrectServiceKnowledge()
  const retire = useRetireServiceKnowledge()
  const promote = usePromoteServiceKnowledge()

  const selectedNode = useMemo(() => {
    const visit = (nodes: ServiceCaseHierarchyNode[]): ServiceCaseHierarchyNode | null => {
      for (const node of nodes) {
        if (sameSelection(selection, node)) return node
        const nested = visit(node.children)
        if (nested) return nested
      }
      return null
    }
    return visit(hierarchy.data ?? [])
  }, [hierarchy.data, selection])
  const selectedBaseProductCount = useMemo(() => new Set(
    (observations.data ?? [])
      .filter((row) => selectedEvidence.includes(row.id))
      .map((row) => row.baseProductId),
  ).size, [observations.data, selectedEvidence])

  useEffect(() => {
    const first = hierarchy.data?.[0]
    if (!selection && first) setSelection({ scope: first.scope, scopeId: first.scopeId })
  }, [hierarchy.data, selection])

  useEffect(() => {
    if (defectCode && !defects.data?.some((row) => row.defectCode === defectCode)) {
      if (lockedPromotion !== null || promote.isPending) return
      setDefectCode(null)
      setPromoteOpen(false)
      setPromotionRequestId(null)
      setLockedPromotion(null)
      setSelectedEvidence([])
      setPromoteStatement('')
    }
  }, [defects.data, defectCode, lockedPromotion, promote.isPending])

  function selectDefect(nextDefectCode: string | null) {
    setDefectCode(nextDefectCode)
    setPromoteOpen(false)
    setPromotionRequestId(null)
    setLockedPromotion(null)
    setSelectedEvidence([])
    setPromoteStatement('')
  }

  function selectNode(node: ServiceCaseHierarchyNode) {
    setSelection({ scope: node.scope, scopeId: node.scopeId })
    selectDefect(null)
  }

  function openReview(action: ReviewAction, item: KnowledgeSummary) {
    setReview({ action, knowledge: item, requestId: crypto.randomUUID() })
    setReason('')
    setStatement(action === 'correct' ? item.safeStatement : '')
  }

  async function submitReview() {
    if (!review) return
    const lockedPayload = review.lockedPayload ?? {
      reason: reason || undefined,
      safeStatement: statement || undefined,
    }
    setReview((current) => current ? { ...current, lockedPayload } : current)
    const input = {
      requestId: review.requestId,
      knowledgeId: review.knowledge.id,
      expectedVersion: review.knowledge.knowledgeVersion,
      ...lockedPayload,
    }
    try {
      const response = review.action === 'approve'
        ? await approve.mutateAsync(input)
        : review.action === 'reject'
          ? await reject.mutateAsync(input)
          : review.action === 'correct'
            ? await correct.mutateAsync(input)
            : await retire.mutateAsync(input)
      toast.success(response?.replayed
        ? 'Die bereits verarbeitete Entscheidung wurde bestaetigt.'
        : 'Produktwissen wurde revisionssicher aktualisiert.')
      setReview(null)
    } catch (error) {
      if (isDecodedConflict(error)) {
        setReview(null)
      } else if (!isTransportError(error)) {
        setReview((current) => current ? {
          ...current,
          requestId: crypto.randomUUID(),
          lockedPayload: undefined,
        } : current)
      }
      toast.error(formatError(error))
    }
  }

  async function submitPromotion() {
    if (selection?.scope !== 'group' || !defectCode || !promotionRequestId) return
    const payload = lockedPromotion ?? {
      safeStatement: promoteStatement,
      observationIds: [...selectedEvidence],
    }
    setLockedPromotion(payload)
    try {
      const response = await promote.mutateAsync({
        requestId: promotionRequestId,
        groupId: selection.scopeId,
        defectCode,
        ...payload,
      })
      toast.success(response?.replayed
        ? 'Der bereits angelegte Gruppenvorschlag wurde bestaetigt.'
        : 'Gruppenwissen wurde als Vorschlag angelegt und muss noch freigegeben werden.')
      setPromoteOpen(false)
      setPromotionRequestId(null)
      setLockedPromotion(null)
      setSelectedEvidence([])
      setPromoteStatement('')
    } catch (error) {
      if (isDecodedConflict(error)) {
        setPromoteOpen(false)
        setPromotionRequestId(null)
        setLockedPromotion(null)
        setSelectedEvidence([])
        setPromoteStatement('')
      } else if (!isTransportError(error)) {
        setPromotionRequestId(crypto.randomUUID())
        setLockedPromotion(null)
      }
      toast.error(formatError(error))
    }
  }

  const reviewPending = approve.isPending || reject.isPending || correct.isPending || retire.isPending
  const reviewCloseBlocked = reviewPending || review?.lockedPayload !== undefined
  const promotionCloseBlocked = promote.isPending || lockedPromotion !== null
  const needsReason = review?.action !== 'approve'
  const canSubmitReview = !!review
    && (!needsReason || reason.trim().length > 0)
    && (review.action !== 'correct' || statement.trim().length > 0)

  function closeReviewDialog() {
    if (!reviewCloseBlocked) setReview(null)
  }

  function closePromotionDialog() {
    if (promotionCloseBlocked) return
    setPromoteOpen(false)
    setPromotionRequestId(null)
    setLockedPromotion(null)
    setSelectedEvidence([])
    setPromoteStatement('')
  }

  return (
    <div className="p-6 space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-ink">Produktwissen / Servicefälle</h1>
          <p className="mt-1 max-w-3xl text-sm text-ink-2">
            Anonymisierte Servicebeobachtungen auswerten und belegtes Produktwissen menschlich prüfen.
            Es werden keine vollständigen E-Mails oder personenbezogenen Rohdaten angezeigt.
          </p>
        </div>
        <Badge variant="outline" className="gap-1"><ShieldCheck /> PII-minimiert</Badge>
      </header>

      <Card className="gap-4 py-4">
        <CardHeader className="px-4">
          <CardTitle>Zeitraum und Defektart</CardTitle>
          <CardDescription>Alle Fallzahlen werden aus den aktuell gültigen, anonymisierten Ledger-Belegen berechnet.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3 px-4">
          <div className="grid gap-1.5">
            <Label htmlFor="service-cases-from">Von</Label>
            <Input id="service-cases-from" type="date" value={draftFilters.from ?? ''}
              onChange={(event) => setDraftFilters((value) => ({ ...value, from: event.target.value || undefined }))} />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="service-cases-to">Bis</Label>
            <Input id="service-cases-to" type="date" value={draftFilters.to ?? ''}
              onChange={(event) => setDraftFilters((value) => ({ ...value, to: event.target.value || undefined }))} />
          </div>
          <div className="grid min-w-56 gap-1.5">
            <Label htmlFor="service-cases-defect">Defektcode (optional)</Label>
            <Input id="service-cases-defect" placeholder="z. B. SCHIENE_KLEMMT"
              value={draftFilters.defectCode ?? ''}
              onChange={(event) => setDraftFilters((value) => ({ ...value, defectCode: event.target.value || undefined }))} />
          </div>
          <Button onClick={() => { setFilters(draftFilters); selectDefect(null) }}>Filter anwenden</Button>
          <Button variant="outline" onClick={() => { setDraftFilters({}); setFilters({}); selectDefect(null) }}>
            Zurücksetzen
          </Button>
        </CardContent>
      </Card>

      <div className="grid gap-5 xl:grid-cols-[minmax(280px,0.8fr)_minmax(0,2.2fr)]">
        <Card className="gap-3 py-4 xl:max-h-[calc(100vh-260px)]">
          <CardHeader className="px-4">
            <CardTitle>Sortiment</CardTitle>
            <CardDescription>Gruppe → Grundartikel → Variante</CardDescription>
          </CardHeader>
          <CardContent className="overflow-y-auto px-2">
            {hierarchy.isLoading && <div className="space-y-2 px-2"><Skeleton className="h-9" /><Skeleton className="h-9" /></div>}
            {hierarchy.isError && <p className="px-2 text-sm text-destructive">Hierarchie konnte nicht geladen werden.</p>}
            {hierarchy.data?.length === 0 && <p className="px-2 text-sm text-muted-foreground">Keine Servicefälle im gewählten Zeitraum.</p>}
            <ul>{hierarchy.data?.map((node) => (
              <HierarchyItem key={`${node.scope}:${node.scopeId}`} node={node} selected={selection} onSelect={selectNode} />
            ))}</ul>
          </CardContent>
        </Card>

        <div className="min-w-0 space-y-5">
          <Card className="gap-4 py-4">
            <CardHeader className="px-4">
              <CardTitle>{selectedNode?.name ?? 'Ebene auswählen'}</CardTitle>
              <CardDescription>
                {selectedNode ? `${SCOPE_LABEL[selectedNode.scope]} · ${selectedNode.caseCount} unabhängige Fälle` : 'Links eine Gruppe oder einen Artikel auswählen.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="px-4">
              {defects.isLoading && <Skeleton className="h-28" />}
              {defects.isError && <p className="text-sm text-destructive">Defektstatistiken konnten nicht geladen werden.</p>}
              {defects.data?.length === 0 && selection && <p className="text-sm text-muted-foreground">Keine Defekte für diese Auswahl.</p>}
              {!!defects.data?.length && (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Defektart</TableHead><TableHead>Fälle</TableHead>
                    <TableHead>Zeitraum</TableHead><TableHead>Monate</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>{defects.data.map((row) => (
                    <TableRow key={row.defectCode} data-state={defectCode === row.defectCode ? 'selected' : undefined}>
                      <TableCell className="p-0 font-medium">
                        <button
                          type="button"
                          aria-pressed={defectCode === row.defectCode}
                          className="w-full rounded-sm px-4 py-3 text-left font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          onClick={() => selectDefect(row.defectCode)}
                        >
                          {row.defectCode}
                        </button>
                      </TableCell>
                      <TableCell>{row.caseCount}</TableCell>
                      <TableCell>{row.firstEventDate} – {row.lastEventDate}</TableCell>
                      <TableCell>{row.monthly.map((month) => `${month.month}: ${month.caseCount}`).join(' · ')}</TableCell>
                    </TableRow>
                  ))}</TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {defectCode && (
            <>
              <Card className="gap-4 py-4">
                <CardHeader className="px-4">
                  <CardTitle className="flex flex-wrap items-center justify-between gap-2">
                    <span>Anonymisierte Belege · {defectCode}</span>
                    {selection?.scope === 'group' && (
                      <Button size="sm" onClick={() => {
                        setPromotionRequestId(crypto.randomUUID())
                        setLockedPromotion(null)
                        setSelectedEvidence([])
                        setPromoteStatement('')
                        setPromoteOpen(true)
                      }}>
                        <BookOpenCheck /> Zu Gruppenwissen hochstufen
                      </Button>
                    )}
                  </CardTitle>
                  <CardDescription>
                    Sichere Kurzkommentare und Handlungsangaben; keine vollständigen Nachrichten oder Kundenidentitäten.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3 px-4">
                  {observations.isLoading && <Skeleton className="h-24" />}
                  {observations.isError && <p className="text-sm text-destructive">Belege konnten nicht geladen werden.</p>}
                  {observations.data?.length === 0 && <p className="text-sm text-muted-foreground">Keine sichtbaren Belege.</p>}
                  {observations.data?.map((row) => (
                    <div key={row.id} className="rounded-lg border p-3">
                      <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        <Badge variant="outline">{row.state === 'anonymized' ? 'Anonymisiert' : 'Geprüft'}</Badge>
                        <span>{row.eventDate}</span>
                        <span>{row.baseProductId}{row.variantId ? ` / ${row.variantId}` : ''}</span>
                        {row.actionCode && <span>Maßnahme: {row.actionCode}</span>}
                      </div>
                      <p className="text-sm">{row.safeComment ?? 'Kein sachlicher Kurzkommentar hinterlegt.'}</p>
                    </div>
                  ))}
                  {observations.hasNextPage && (
                    <Button variant="outline" disabled={observations.isFetchingNextPage}
                      onClick={() => void observations.fetchNextPage()}>
                      Weitere Belege laden
                    </Button>
                  )}
                </CardContent>
              </Card>

              <Card className="gap-4 py-4">
                <CardHeader className="px-4">
                  <CardTitle>Geprüftes Produktwissen</CardTitle>
                  <CardDescription>Jede Entscheidung und Korrektur bleibt als unveränderbares Review-Ereignis nachvollziehbar.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3 px-4">
                  {knowledge.isLoading && <Skeleton className="h-24" />}
                  {knowledge.isError && <p className="text-sm text-destructive">Produktwissen konnte nicht geladen werden.</p>}
                  {knowledge.data?.length === 0 && <p className="text-sm text-muted-foreground">Noch kein Wissen zu diesem Defekt.</p>}
                  {knowledge.data?.map((item) => (
                    <div key={item.id} className="rounded-lg border p-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <StatusBadge status={item.status} />
                        <Badge variant="outline">Version {item.knowledgeVersion}</Badge>
                        <span className="text-xs text-muted-foreground">
                          {item.independentCaseCount} Fälle · {item.independentProductCount} Grundartikel
                        </span>
                      </div>
                      <p className="mt-3 text-sm">{item.safeStatement}</p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {item.status === 'proposed' && <Button size="sm" onClick={() => openReview('approve', item)}>Freigeben</Button>}
                        {item.status === 'proposed' && <Button size="sm" variant="destructive" onClick={() => openReview('reject', item)}>Ablehnen</Button>}
                        {item.status !== 'retired' && <Button size="sm" variant="outline" onClick={() => openReview('correct', item)}>Korrigieren</Button>}
                        {item.status === 'approved' && <Button size="sm" variant="outline" onClick={() => openReview('retire', item)}>Stilllegen</Button>}
                      </div>
                      {item.events.length > 0 && (
                        <details className="mt-3 text-xs text-muted-foreground">
                          <summary className="cursor-pointer">Review-Verlauf ({item.events.length})</summary>
                          <ul className="mt-2 space-y-1">
                            {item.events.map((event) => (
                              <li key={event.id}>{event.createdAt} · {event.eventType} · Version {event.toVersion}{event.reason ? ` · ${event.reason}` : ''}</li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </div>
                  ))}
                  {knowledge.hasNextPage && (
                    <Button variant="outline" disabled={knowledge.isFetchingNextPage}
                      onClick={() => void knowledge.fetchNextPage()}>
                      Weiteres Produktwissen laden
                    </Button>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>

      <Dialog open={review !== null} onOpenChange={(open) => { if (!open) closeReviewDialog() }}>
        <DialogContent
          className={cn(reviewCloseBlocked && '[&>[data-slot=dialog-close]]:hidden')}
          onEscapeKeyDown={(event) => { if (reviewCloseBlocked) event.preventDefault() }}
          onInteractOutside={(event) => { if (reviewCloseBlocked) event.preventDefault() }}
        >
          <DialogHeader>
            <DialogTitle>Produktwissen {review ? {
              approve: 'freigeben', reject: 'ablehnen', correct: 'korrigieren', retire: 'stilllegen',
            }[review.action] : ''}</DialogTitle>
            <DialogDescription>
              Die Entscheidung wird mit Version, Zeitpunkt und Benutzer revisionssicher protokolliert.
            </DialogDescription>
          </DialogHeader>
          {review?.action === 'correct' && <div className="grid gap-2">
            <Label htmlFor="review-statement">Korrigierte, anonymisierte Aussage</Label>
            <Textarea id="review-statement" value={statement} disabled={review.lockedPayload !== undefined}
              onChange={(event) => setStatement(event.target.value)} />
          </div>}
          {needsReason && <div className="grid gap-2">
            <Label htmlFor="review-reason">Sachliche Begründung</Label>
            <Textarea id="review-reason" value={reason} disabled={review?.lockedPayload !== undefined}
              onChange={(event) => setReason(event.target.value)}
              placeholder="Keine Namen, E-Mail-Adressen oder andere personenbezogene Angaben." />
          </div>}
          {review?.lockedPayload !== undefined && !reviewPending && (
            <p role="alert" className="text-sm text-amber-700">
              Ausgang unklar: Erneut senden bestaetigt exakt dieselbe Entscheidung. Der Dialog bleibt bis zur Klaerung geoeffnet.
              Wenn der Dienst laenger nicht erreichbar ist, laedt ein Seiten-Reload den gespeicherten Stand neu.
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" disabled={reviewCloseBlocked} onClick={closeReviewDialog}>Abbrechen</Button>
            <Button disabled={!canSubmitReview || reviewPending} onClick={() => void submitReview()}>Entscheidung speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={promoteOpen} onOpenChange={(open) => {
        if (open) setPromoteOpen(true)
        else closePromotionDialog()
      }}>
        <DialogContent
          className={cn(
            'sm:max-w-2xl max-h-[90vh] overflow-y-auto',
            promotionCloseBlocked && '[&>[data-slot=dialog-close]]:hidden',
          )}
          onEscapeKeyDown={(event) => { if (promotionCloseBlocked) event.preventDefault() }}
          onInteractOutside={(event) => { if (promotionCloseBlocked) event.preventDefault() }}
        >
          <DialogHeader>
            <DialogTitle>Belegtes Gruppenwissen vorschlagen</DialogTitle>
            <DialogDescription>
              Mindestens zwei unabhängige Fälle aus zwei verschiedenen Grundartikeln auswählen. Der Vorschlag wird nicht automatisch freigegeben.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            <CircleAlert className="mr-2 inline size-4" />
            Nur anonymisierte, sachliche Aussagen eintragen. Die Datenbank prüft die Evidenzschwelle erneut.
          </div>
          <div className="grid gap-2">
            <Label htmlFor="promotion-statement">Anonymisierte Gruppenaussage</Label>
            <Textarea id="promotion-statement" value={promoteStatement}
              disabled={lockedPromotion !== null}
              onChange={(event) => setPromoteStatement(event.target.value)} />
          </div>
          <div className="grid gap-2">
            <Label>
              Belege ({selectedEvidence.length} ausgewählt · {selectedBaseProductCount} Grundartikel)
            </Label>
            {observations.data?.map((row) => (
              <label key={row.id} className="flex items-start gap-3 rounded-md border p-3 text-sm">
                <Checkbox checked={selectedEvidence.includes(row.id)} disabled={lockedPromotion !== null} onCheckedChange={(checked) => {
                  setSelectedEvidence((values) => checked
                    ? [...new Set([...values, row.id])]
                    : values.filter((id) => id !== row.id))
                }} />
                <span><span className="font-medium">{row.baseProductId}</span> · {row.safeComment ?? 'Kein Kurzkommentar'}</span>
              </label>
            ))}
          </div>
          {lockedPromotion !== null && !promote.isPending && (
            <p role="alert" className="text-sm text-amber-700">
              Ausgang unklar: Erneut senden verwendet exakt dieselben Belege und denselben Text.
              Wenn der Dienst laenger nicht erreichbar ist, laedt ein Seiten-Reload den gespeicherten Stand neu.
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" disabled={promotionCloseBlocked} onClick={closePromotionDialog}>Abbrechen</Button>
            <Button disabled={promote.isPending || (lockedPromotion === null
              && (selectedEvidence.length < 2 || selectedBaseProductCount < 2
                || promoteStatement.trim().length === 0))}
              onClick={() => void submitPromotion()}>
              Als Vorschlag anlegen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
